// Audio System Module

// Production logging utility
const log = {
    debug: (message, ...args) => {
        if (process.env.NODE_ENV === 'development') {
            console.log(message, ...args);
        }
    },
    error: (message, ...args) => console.error(message, ...args),
    warn: (message, ...args) => console.warn(message, ...args)
};

export class AudioSystem {
    constructor() {
        this.audioContext = null;
        this.seaBuffer = null;
        this.seagullBuffer = null;
        this.seaDecodedBuffer = null; // Store decoded sea audio buffer
        this.seagullDecodedBuffer = null; // Store decoded seagull audio buffer
        this.seaSource = null;
        this.seagullSource = null;
        this.seaGain = null;
        this.seagullGain = null;
        this.isEnabled = false;
        this.isMuted = false;
        this.seagullInterval = null;
        this.seaVolume = 0.3; // 20% volume for sea
        this.seagullVolume = 0.5; // 50% volume for seagull
        this.userPreference = null; // Store user's sound preference
        this.hasUserMadeChoice = false; // Track if user has made a choice
        
        // Initialize audio context on first user interaction
        this.initAudioContext = this.initAudioContext.bind(this);
        this.enableAudio = this.enableAudio.bind(this);
        this.disableAudio = this.disableAudio.bind(this);
        this.toggleMute = this.toggleMute.bind(this);
        this.setSeaVolume = this.setSeaVolume.bind(this);
        this.setSeagullVolume = this.setSeagullVolume.bind(this);
        this.saveUserPreference = this.saveUserPreference.bind(this);
        this.loadUserPreference = this.loadUserPreference.bind(this);
    }

    async init() {
        try {
            // Load user preference from localStorage
            this.loadUserPreference();
            
            // Load audio files with timeout
            const audioLoadPromise = this.loadAudioFiles();
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Audio loading timeout')), 10000)
            );
            
            await Promise.race([audioLoadPromise, timeoutPromise]);
            
            // Set up event listeners for user interaction
            this.setupEventListeners();
            
            log.debug('Audio system initialized successfully');
            return true;
        } catch (error) {
            log.error('Failed to initialize audio system:', error);
            // Graceful degradation - continue without audio
            this.isEnabled = false;
            return false;
        }
    }
    
    // Cleanup method for proper resource management
    destroy() {
        try {
            // Stop all playing sounds
            this.disableAudio();
            
            // Clear intervals
            if (this.seagullInterval) {
                clearInterval(this.seagullInterval);
                this.seagullInterval = null;
            }
            
            // Close audio context
            if (this.audioContext && this.audioContext.state !== 'closed') {
                this.audioContext.close();
            }
            
            // Clear references
            this.seaBuffer = null;
            this.seagullBuffer = null;
            this.seaDecodedBuffer = null;
            this.seagullDecodedBuffer = null;
            
        } catch (error) {
            log.error('Error during audio system cleanup:', error);
        }
    }

    saveUserPreference(preference) {
        try {
            const soundPreferences = {
                enabled: preference === 'enabled',
                timestamp: Date.now(),
                version: '1.0'
            };
            localStorage.setItem('chromaticOne_soundPreference', JSON.stringify(soundPreferences));
            this.userPreference = preference;
            this.hasUserMadeChoice = true;
            log.debug('Sound preference saved:', preference);
        } catch (error) {
            log.error('Failed to save sound preference:', error);
        }
    }

    loadUserPreference() {
        try {
            const saved = localStorage.getItem('chromaticOne_soundPreference');
            if (saved) {
                const preferences = JSON.parse(saved);
                this.userPreference = preferences.enabled ? 'enabled' : 'disabled';
                this.hasUserMadeChoice = true;
                log.debug('Sound preference loaded:', this.userPreference);
                
                // Auto-enable audio if user previously enabled it
                if (preferences.enabled) {
                    // We'll enable audio after user interaction
                    this.pendingEnable = true;
                }
            }
        } catch (error) {
            console.error('Failed to load sound preference:', error);
        }
    }

    shouldShowPopup() {
        return !this.hasUserMadeChoice;
    }

    async loadAudioFiles() {
        try {
            // Load sea sound
            this.seaBuffer = await this.loadAudioFile('/audio/sea.mp3');
            log.debug('Sea audio loaded');
            
            // Load seagull sound
            this.seagullBuffer = await this.loadAudioFile('/audio/seagull.mp3');
            log.debug('Seagull audio loaded');
            
        } catch (error) {
            console.error('Error loading audio files:', error);
            throw error;
        }
    }

    async decodeAudioBuffers() {
        console.log('decodeAudioBuffers called:', {
            audioContext: !!this.audioContext,
            seaBuffer: !!this.seaBuffer,
            seagullBuffer: !!this.seagullBuffer,
            seaDecodedBuffer: !!this.seaDecodedBuffer,
            seagullDecodedBuffer: !!this.seagullDecodedBuffer
        });
        
        if (!this.audioContext) {
            log.debug('No audio context available for decoding');
            return;
        }
        
        try {
            // Decode sea audio buffer
            if (this.seaBuffer && !this.seaDecodedBuffer) {
                console.log('Decoding sea audio buffer...');
                this.seaDecodedBuffer = await this.decodeAudioData(this.seaBuffer);
                console.log('Sea audio decoded successfully');
            } else {
                console.log('Sea buffer already decoded or not available');
            }
            
            // Decode seagull audio buffer
            if (this.seagullBuffer && !this.seagullDecodedBuffer) {
                console.log('Decoding seagull audio buffer...');
                this.seagullDecodedBuffer = await this.decodeAudioData(this.seagullBuffer);
                console.log('Seagull audio decoded successfully');
            } else {
                console.log('Seagull buffer already decoded or not available');
            }
        } catch (error) {
            console.error('Error decoding audio buffers:', error);
        }
    }

    decodeAudioData(arrayBuffer) {
        return new Promise((resolve, reject) => {
            this.audioContext.decodeAudioData(arrayBuffer, resolve, reject);
        });
    }

    async loadAudioFile(url) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.responseType = 'arraybuffer';
            
            xhr.onload = () => {
                if (xhr.status === 200) {
                    resolve(xhr.response);
                } else {
                    reject(new Error(`Failed to load audio file: ${xhr.status}`));
                }
            };
            
            xhr.onerror = () => {
                reject(new Error('Network error loading audio file'));
            };
            
            xhr.send();
        });
    }

    async initAudioContext() {
        if (this.audioContext) return;
        
        try {
            // Create audio context
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            
            // Create gain nodes for volume control
            this.seaGain = this.audioContext.createGain();
            this.seagullGain = this.audioContext.createGain();
            
            // Connect gain nodes to destination
            this.seaGain.connect(this.audioContext.destination);
            this.seagullGain.connect(this.audioContext.destination);
            
            // Set initial volumes
            this.seaGain.gain.value = this.seaVolume;
            this.seagullGain.gain.value = this.seagullVolume;
            
            // Decode audio buffers
            await this.decodeAudioBuffers();
            
            log.debug('Audio context initialized');
        } catch (error) {
            console.error('Failed to initialize audio context:', error);
        }
    }

    setupEventListeners() {
        // Enable audio on first user interaction
        const enableAudioOnInteraction = async () => {
            await this.initAudioContext();
            
            // If user previously enabled audio, enable it now
            if (this.pendingEnable) {
                this.enableAudio();
                this.pendingEnable = false;
            }
            
            document.removeEventListener('click', enableAudioOnInteraction);
            document.removeEventListener('touchstart', enableAudioOnInteraction);
            document.removeEventListener('keydown', enableAudioOnInteraction);
        };
        
        document.addEventListener('click', enableAudioOnInteraction);
        document.addEventListener('touchstart', enableAudioOnInteraction);
        document.addEventListener('keydown', enableAudioOnInteraction);
    }

    async enableAudio() {
        if (this.isEnabled) return;
        
        await this.initAudioContext();
        this.isEnabled = true;
        this.isMuted = false;
        
        // Start sea sound loop
        this.startSeaSound();
        
        // Start seagull sounds
        this.startSeagullSounds();
        
        // Save user preference
        this.saveUserPreference('enabled');
        
        // Update UI
        this.updateSoundToggleUI();
        
        console.log('Audio enabled');
    }

    disableAudio() {
        if (!this.isEnabled) return;
        
        this.isEnabled = false;
        this.isMuted = false;
        
        // Stop all sounds
        this.stopSeaSound();
        this.stopSeagullSounds();
        
        // Save user preference
        this.saveUserPreference('disabled');
        
        // Update UI
        this.updateSoundToggleUI();
        
        console.log('Audio disabled');
    }

    toggleMute() {
        if (!this.isEnabled) return;
        
        this.isMuted = !this.isMuted;
        
        if (this.isMuted) {
            this.seaGain.gain.value = 0;
            this.seagullGain.gain.value = 0;
        } else {
            this.seaGain.gain.value = this.seaVolume;
            this.seagullGain.gain.value = this.seagullVolume;
        }
        
        // Update UI
        this.updateSoundToggleUI();
        
        console.log('Audio muted:', this.isMuted);
    }

    startSeaSound() {
        console.log('startSeaSound called:', {
            audioContext: !!this.audioContext,
            seaDecodedBuffer: !!this.seaDecodedBuffer,
            seaSource: !!this.seaSource,
            isEnabled: this.isEnabled,
            isMuted: this.isMuted
        });
        
        if (!this.audioContext || !this.seaDecodedBuffer || this.seaSource) {
            console.log('startSeaSound early return - missing requirements');
            return;
        }
        
        try {
            this.playSeaLoop(this.seaDecodedBuffer);
        } catch (error) {
            console.error('Error starting sea sound:', error);
        }
    }

    playSeaLoop(buffer) {
        if (!this.audioContext || !this.seaGain) return;
        
        this.seaSource = this.audioContext.createBufferSource();
        this.seaSource.buffer = buffer;
        this.seaSource.loop = true;
        this.seaSource.connect(this.seaGain);
        
        // Set volume
        this.seaGain.gain.value = this.isMuted ? 0 : this.seaVolume;
        
        this.seaSource.start(0);
        console.log('Sea sound started');
    }

    stopSeaSound() {
        if (this.seaSource) {
            try {
                this.seaSource.stop();
            } catch (error) {
                // Source might already be stopped
            }
            this.seaSource = null;
        }
    }

    startSeagullSounds() {
        if (!this.audioContext || !this.seagullBuffer) return;
        
        // Clear any existing interval
        if (this.seagullInterval) {
            clearInterval(this.seagullInterval);
        }
        
        // Play seagull sounds every 10 seconds with 70% probability
        this.seagullInterval = setInterval(() => {
            if (Math.random() < 0.7) { // 70% probability
                this.playSeagullSound();
            }
        }, 10000);
        
        console.log('Seagull sounds scheduled');
    }

    playSeagullSound() {
        if (!this.audioContext || !this.seagullDecodedBuffer || this.isMuted) return;
        
        try {
            const source = this.audioContext.createBufferSource();
            source.buffer = this.seagullDecodedBuffer;
            source.connect(this.seagullGain);
            source.start(0);
            console.log('Seagull sound played');
        } catch (error) {
            console.error('Error playing seagull sound:', error);
        }
    }

    stopSeagullSounds() {
        if (this.seagullInterval) {
            clearInterval(this.seagullInterval);
            this.seagullInterval = null;
        }
    }

    setSeaVolume(volume) {
        this.seaVolume = Math.max(0, Math.min(1, volume));
        if (this.seaGain && !this.isMuted) {
            this.seaGain.gain.value = this.seaVolume;
        }
    }

    setSeagullVolume(volume) {
        this.seagullVolume = Math.max(0, Math.min(1, volume));
        if (this.seagullGain && !this.isMuted) {
            this.seagullGain.gain.value = this.seagullVolume;
        }
    }

    updateSoundToggleUI() {
        const soundToggle = document.getElementById('sound-toggle');
        const soundIcon = soundToggle?.querySelector('.sound-icon');
        
        if (!soundToggle || !soundIcon) return;
        
        if (!this.isEnabled || this.isMuted) {
            // Show muted icon
            soundIcon.src = '/images/sound-muted.png';
            soundToggle.classList.add('muted');
        } else {
            // Show unmuted icon
            soundIcon.src = '/images/sound.png';
            soundToggle.classList.remove('muted');
        }
    }

    // Public methods for external control
    pause() {
        if (this.isEnabled && !this.isMuted) {
            this.toggleMute();
        }
    }

    resume() {
        if (this.isEnabled && this.isMuted) {
            this.toggleMute();
        }
    }

    stop() {
        this.disableAudio();
    }

    getStatus() {
        return {
            enabled: this.isEnabled,
            muted: this.isMuted,
            seaVolume: this.seaVolume,
            seagullVolume: this.seagullVolume,
            userPreference: this.userPreference,
            hasUserMadeChoice: this.hasUserMadeChoice
        };
    }

    getUserPreference() {
        return this.userPreference;
    }
}
